import { Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	FloatingPanel,
	FloatingPanelContent,
	FloatingPanelDescription,
	FloatingPanelHeader,
	FloatingPanelTitle,
} from "@/components/Core/FloatingPanel";
import Checkbox from "@/components/Core/Checkbox/Checkbox";
import AppAlertDialog from "@/components/Core/AppAlertDialog";
import AvailabilityHeader from "@/components/Shared/AvailabilityHeader/AvailabilityHeader";
import LoadingIndicator from "@/components/Shared/LoadingIndicator/LoadingIndicator";
import { ShiftPlannerConsultant, ShiftPlannerShift } from "@/components/Shared/ShiftPlanner";
import ListViewTagChips from "@/components/Shared/TagChip/ListViewTagChips";
import useDebounce from "@/hooks/useDebounce";
import Job, { Shift } from "@/models/Job";
import { MatchingConsultant } from "@/models/User";
import { useMatchingConsultants } from "@/networking/job.hooks";
import { useOrganisation } from "@/networking/organisation.hooks";
import { cn } from "@/lib/utils";
import { weekdaysToQuery } from "@/utils/availability.utils";
import { decimalToPercentage } from "@/utils/string.utils";
import { plannerShiftInterval } from "../jobMatching.utils";
import AvailabilityStrip from "./AvailabilityStrip";
import MatchingFilterBar from "./MatchingFilterBar";
import StatusPill from "./StatusPill";
import {
	CompetenceFilter,
	CredentialFilter,
	JobCandidate,
	deriveCandidates,
	isEmptyCredentialFilter,
	missingCredentials,
	requirementsToCredentialFilter,
	toCompetenceFilter,
} from "./jobConsultantPanel.utils";

export interface JobConsultantPanelProps {
	job: Job;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Ids of the draft shifts being staffed. Empty = no specific target. */
	targetShiftIds: string[];
	/** The current planner draft (row statuses + candidate derivation). */
	draftShifts: ShiftPlannerShift[];
	/** Server shifts incl. applications/invitations (candidate derivation). */
	serverShifts: Shift[];
	/** id → display name for everyone the planner knows about. */
	consultantNameById: Map<string, string>;
	/** Book the consultant on the given shifts (draft edit). */
	onBook: (consultant: ShiftPlannerConsultant, shiftIds: string[]) => void;
	/** Remove the consultant's booking from the given shifts (draft edit). */
	onUnbook: (consultantId: string, shiftIds: string[]) => void;
	/** Replace the invited set on the target shifts (draft edit). */
	onInviteChange: (consultantIds: string[]) => void;
	/** Remove every draft invitation of the consultant, on any shift (draft edit). */
	onUninvite: (consultantId: string) => void;
}

const inviteButtonClasses = (active: boolean) =>
	cn(
		"rounded border border-solid px-2.5 py-1 text-xs font-medium",
		active
			? "border-orange-400 bg-orange-400 text-white hover:bg-orange-500"
			: "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
	);

const bookButtonClasses =
	"rounded border-0 bg-success px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40";

const unbookButtonClasses =
	"rounded border border-solid border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-danger hover:bg-gray-50";

/**
 * The Schema tab's consultant picker: a {@link FloatingPanel} over the shift
 * planner with a near-transparent backdrop that blocks the grid behind it
 * (clicking the backdrop closes the panel) — with two views —
 *
 * - **Matcha**: server-side matching search for the targeted shifts, further
 *   constrained by an editable credential filter seeded from the job's
 *   matching gate (`job.requirements`). Relaxing the filter surfaces
 *   almost-matching consultants (they otherwise collapse under "Uppfyller inte
 *   kraven"); one click resets back to the job's requirements.
 * - **Kandidater**: everyone already connected to the job — booked and invited
 *   per the current draft, applicants and declined invitations per the server.
 *
 * Book/invite actions are DRAFT edits via the parent's callbacks (saved with
 * the planner's save bar), mirroring the planner's editing model.
 */
const JobConsultantPanel: React.FC<JobConsultantPanelProps> = ({
	job,
	open,
	onOpenChange,
	targetShiftIds,
	draftShifts,
	serverShifts,
	consultantNameById,
	onBook,
	onUnbook,
	onInviteChange,
	onUninvite,
}) => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();

	const [view, setView] = useState<"match" | "candidates">(
		targetShiftIds.length ? "match" : "candidates"
	);

	// The panel stays mounted between openings — re-pick the natural tab each
	// time it opens: staffing a selection → Matcha, browsing the job → Kandidater.
	useEffect(() => {
		if (open) setView(targetShiftIds.length ? "match" : "candidates");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const [searchTerm, setSearchTerm] = useState("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [filterCustomerExperience, setFilterCustomerExperience] = useState(false);
	const [filterInvited, setFilterInvited] = useState(false);
	const [filteredDays, setFilteredDays] = useState<number[]>([]);

	const jobRequirementsFilter = useMemo(
		() => requirementsToCredentialFilter(job.requirements),
		[job.requirements]
	);
	const [credentialFilter, setCredentialFilter] = useState<CredentialFilter>(jobRequirementsFilter);

	const jobCompetenceFilter = useMemo(() => toCompetenceFilter(job.competence), [job.competence]);
	const [competenceFilter, setCompetenceFilter] = useState<CompetenceFilter | undefined>(
		jobCompetenceFilter
	);

	const targets = useMemo(
		() => draftShifts.filter((shift) => targetShiftIds.includes(shift.id)),
		[draftShifts, targetShiftIds]
	);
	const targetInvitedIds = useMemo(
		() => [...new Set(targets.flatMap((shift) => shift.invitedConsultantIds ?? []))],
		[targets]
	);
	const targetBookedIds = useMemo(
		() => new Set(targets.map((shift) => shift.consultantId).filter(Boolean) as string[]),
		[targets]
	);

	// The hook sets `initialData: []`, so the query is never in `isLoading` —
	// `isFetching` is the only signal that a load is in flight.
	const { data: consultants, isFetching } = useMatchingConsultants(
		{
			jobId: job.id,
			availability: organisation ? weekdaysToQuery(filteredDays) : undefined,
			branch: competenceFilter?.branch ?? "any",
			service: competenceFilter?.service ?? "any",
			skills: competenceFilter?.skills.length ? competenceFilter.skills.join(",") : undefined,
			hasCustomerExperience: filterCustomerExperience + "",
			isInvited: filterInvited + "",
			keyword: debouncedSearchTerm.trim(),
			pageSize: 500,
			// Same rule as the legacy sidebar: with concrete time slots the backend
			// filters on real conflicts, so booked/pending consultants only make
			// sense to include when browsing without a target.
			includeBookedConsultants: targets.length === 0,
			includePendingApplications: targets.length === 0,
			timeSlots: targets.length
				? targets
						.map((shift) => {
							const { start, end } = plannerShiftInterval(shift);
							return `${start} - ${end}`;
						})
						.join(",")
				: undefined,
			expand: "guaranteedHours",
		},
		{ enabled: open, refetchOnWindowFocus: false }
	);

	// Split by the credential filter: passing consultants make the main list,
	// the rest collapse under "Uppfyller inte kraven" with what they lack.
	// Only consultants passing the credential filter are listed — relax the
	// filter chips to widen the match.
	const matching = useMemo(() => {
		const rank = (c: MatchingConsultant) => {
			if (targetBookedIds.has(c.id)) return 0;
			if (c.hasPendingApplication) return 1;
			if (targetInvitedIds.includes(c.id) || c.isInvited) return 2;
			if (c.hasCustomerExperience) return 3;
			return 4;
		};
		return [...(consultants || [])]
			.filter((consultant) =>
				isEmptyCredentialFilter(missingCredentials(consultant, credentialFilter))
			)
			.sort(
				(a, b) =>
					rank(a) - rank(b) ||
					`${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, "sv")
			);
	}, [consultants, credentialFilter, targetBookedIds, targetInvitedIds]);

	const candidates = useMemo(
		() => deriveCandidates(draftShifts, serverShifts, consultantNameById),
		[draftShifts, serverShifts, consultantNameById]
	);

	// Inviting while a target shift has a booked consultant unbooks them —
	// confirmed via AppAlertDialog first (the id parks here while it is open)
	const [pendingInviteId, setPendingInviteId] = useState<string | null>(null);

	function toggleAvailabilityDay(day: number) {
		setFilteredDays((prev) =>
			prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
		);
	}

	function toggleInvite(consultant: MatchingConsultant) {
		const isInvited = targetInvitedIds.includes(consultant.id);
		if (isInvited) {
			onInviteChange(targetInvitedIds.filter((id) => id !== consultant.id));
			return;
		}
		if (targetBookedIds.size > 0) {
			setPendingInviteId(consultant.id);
			return;
		}
		onInviteChange([...targetInvitedIds, consultant.id]);
	}

	function bookConsultant(consultant: MatchingConsultant) {
		onBook(
			{ id: consultant.id, name: `${consultant.firstName} ${consultant.lastName}` },
			targetShiftIds
		);
		// Booking finishes the errand — close the panel. Inviting keeps it open
		// so several consultants can be invited in one go.
		onOpenChange(false);
	}

	const hasTargets = targets.length > 0;

	function renderMatchRow(consultant: MatchingConsultant): React.ReactElement {
		const isBookedOnTargets = targetBookedIds.has(consultant.id);
		const isInvited = targetInvitedIds.includes(consultant.id) || !!consultant.isInvited;
		const city = consultant.profile?.address?.city;
		const lowTg = consultant.tg != null && consultant.tg < 0.3;

		return (
			<div
				key={consultant.id}
				data-contact={consultant.id}
				className={cn(
					"flex flex-col gap-1.5 border-b border-gray-100 px-5 py-3",
					consultant.blocked && "bg-red-50"
				)}
			>
				<div className="flex items-center justify-between gap-2">
					<div className="flex min-w-0 items-center gap-1.5">
						<span className="truncate text-sm font-medium text-gray-900">
							{consultant.firstName} {consultant.lastName}
						</span>
						{consultant.hasCustomerExperience && (
							<StatusPill color="orange">{t("job.customerExperienceShort")}</StatusPill>
						)}
						{consultant.blocked && <StatusPill color="red">Blockerad</StatusPill>}
						{isBookedOnTargets && <StatusPill color="green">{t("job.isBooked" as any)}</StatusPill>}
						{consultant.hasPendingApplication && (
							<StatusPill color="blue">{t("job.jobStatus.pending")}</StatusPill>
						)}
					</div>

					<div className="flex shrink-0 items-center gap-1.5">
						{!isBookedOnTargets && !consultant.blocked && (
							<>
								<button
									type="button"
									disabled={!hasTargets}
									title={
										hasTargets ? undefined : (t("consultantPanel.selectShiftsFirst") as string)
									}
									onClick={() => toggleInvite(consultant)}
									className={cn(inviteButtonClasses(isInvited), !hasTargets && "opacity-40")}
								>
									{isInvited ? t("job.invited") : t("job.invite")}
								</button>
								<button
									type="button"
									disabled={!hasTargets}
									title={
										hasTargets ? undefined : (t("consultantPanel.selectShiftsFirst") as string)
									}
									onClick={() => bookConsultant(consultant)}
									className={bookButtonClasses}
								>
									{t("job.action.book")}
								</button>
							</>
						)}
						{isBookedOnTargets && (
							<button
								type="button"
								onClick={() => onUnbook(consultant.id, targetShiftIds)}
								className={unbookButtonClasses}
							>
								{t("job.action.cancelApplication")}
							</button>
						)}
					</div>
				</div>

				<div className="flex items-center justify-between gap-2">
					<div className="flex min-w-0 items-center gap-2 text-xs text-gray-500">
						{city && <span className="truncate">{city}</span>}
						<span
							className={cn(
								"rounded px-1 py-0.5 tabular-nums",
								lowTg ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"
							)}
						>
							TG {decimalToPercentage(consultant.tg)}
						</span>
						<ListViewTagChips
							tags={consultant.profile?.tags}
							tagTypes={organisation?.consultantTagTypes}
							certifications={consultant.profile?.certifications}
							certificationTypes={organisation?.certificationTypes}
							maxVisible={2}
						/>
					</div>
					<AvailabilityStrip
						availability={consultant.profile?.availability}
						organisation={organisation}
					/>
				</div>
			</div>
		);
	}

	function renderCandidateRow(candidate: JobCandidate): React.ReactElement {
		// Book applicants only onto their applied shifts that are still unmanned
		const bookableShiftIds = candidate.appliedShiftIds.filter((shiftId) =>
			draftShifts.some((shift) => shift.id === shiftId && !shift.consultantId && !shift.absence)
		);

		return (
			<div
				key={candidate.id}
				data-contact={candidate.id}
				className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3"
			>
				<div className="flex min-w-0 flex-col gap-1">
					<span className="truncate text-sm font-medium text-gray-900">{candidate.name}</span>
					<div className="flex flex-wrap items-center gap-1">
						{candidate.bookedShiftIds.length > 0 && (
							<StatusPill color="green">
								{t("job.isBooked" as any)} · {candidate.bookedShiftIds.length}{" "}
								{t("consultantPanel.shiftsUnit")}
							</StatusPill>
						)}
						{candidate.appliedShiftIds.length > 0 && (
							<StatusPill color="blue">
								{t("job.hasApplied")} · {candidate.appliedShiftIds.length}
							</StatusPill>
						)}
						{candidate.invitedShiftIds.length > 0 && (
							<StatusPill color="orange">
								{t("job.invited")} · {candidate.invitedShiftIds.length}
							</StatusPill>
						)}
						{candidate.declinedShiftIds.length > 0 && (
							<StatusPill color="red">{t("job.inviteDeclined")}</StatusPill>
						)}
					</div>
				</div>

				<div className="flex shrink-0 items-center gap-1.5">
					{bookableShiftIds.length > 0 && (
						<button
							type="button"
							onClick={() => {
								onBook({ id: candidate.id, name: candidate.name }, bookableShiftIds);
								onOpenChange(false);
							}}
							className={bookButtonClasses}
						>
							{t("job.action.book")}
						</button>
					)}
					{candidate.invitedShiftIds.length > 0 && (
						<button
							type="button"
							onClick={() => onUninvite(candidate.id)}
							className={unbookButtonClasses}
						>
							{t("consultantPanel.uninvite")}
						</button>
					)}
				</div>
			</div>
		);
	}

	return (
		<FloatingPanel open={open} onOpenChange={onOpenChange}>
			{/* NON-modal + backdrop: the near-transparent overlay covers the
			    grid so it can't be edited underneath the picker, and clicking
			    it closes the panel. (A modal root would pointer-lock the
			    panel's own portaled dropdowns — the competence selector.) */}
			<FloatingPanelContent side="right" size="wide" overlay overlayClassName="bg-black/10">
				<FloatingPanelHeader className="pb-3">
					<div>
						<FloatingPanelTitle>
							{hasTargets
								? t("consultantPanel.selectConsultantsFor", { count: targets.length })
								: t("CONSULTANTS")}
						</FloatingPanelTitle>
						{!hasTargets && (
							<FloatingPanelDescription>{t("consultantPanel.wholeJob")}</FloatingPanelDescription>
						)}
					</div>
				</FloatingPanelHeader>

				{view === "match" ? (
					<div className="flex min-h-0 flex-1 flex-col">
						<div className="flex flex-col gap-2.5 border-b border-gray-200 px-5 py-3">
							<div className="relative">
								<Search
									size={14}
									className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
								/>
								<input
									type="text"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									placeholder={t("SEARCH") as string}
									className="w-full rounded-md border border-solid border-gray-300 bg-white py-1.5 pl-8 pr-2.5 text-sm text-gray-800 outline-none focus:border-brand"
								/>
							</div>

							<MatchingFilterBar
								organisation={organisation}
								competenceFilter={competenceFilter}
								onCompetenceFilterChange={setCompetenceFilter}
								credentialFilter={credentialFilter}
								onCredentialFilterChange={setCredentialFilter}
								jobCompetenceFilter={jobCompetenceFilter}
								jobRequirementsFilter={jobRequirementsFilter}
							/>

							<div className="flex flex-wrap items-center justify-between gap-4">
								<div className="flex flex-wrap items-center gap-4">
									<Checkbox
										customStyle
										labelClassName="text-sm"
										label={t("job.customerExperience")}
										isChecked={filterCustomerExperience}
										onChange={(e: any) => setFilterCustomerExperience(e.target.checked)}
									/>
									<Checkbox
										customStyle
										labelClassName="text-sm"
										label={t("INVITED")}
										isChecked={filterInvited}
										onChange={(e: any) => setFilterInvited(e.target.checked)}
									/>
								</div>
								{/* Sits above the rows' availability strips, like the legacy
								    sidebar: click a weekday to require availability that day */}
								<div className="w-[200px] text-xs text-gray-600">
									<AvailabilityHeader onClick={toggleAvailabilityDay} selectedDays={filteredDays} />
								</div>
							</div>
						</div>

						<div className="min-h-0 flex-1 overflow-y-auto">
							{/* Existing rows stay visible during refetches; the spinner only
							    covers loads with nothing to show yet. */}
							{isFetching && matching.length === 0 ? (
								<div className="p-8">
									<LoadingIndicator size="s" showText={false} />
								</div>
							) : (
								<>
									{matching.map((consultant) => renderMatchRow(consultant))}
									{matching.length === 0 && (
										<p className="px-5 py-6 text-center text-sm text-gray-500">
											{t("NO_CONSULTANTS_MESSAGE")}
										</p>
									)}
								</>
							)}
						</div>
					</div>
				) : (
					<div className="min-h-0 flex-1 overflow-y-auto">
						{candidates.length === 0 ? (
							<p className="px-5 py-6 text-center text-sm text-gray-500">
								{t("consultantPanel.noCandidates")}
							</p>
						) : (
							candidates.map(renderCandidateRow)
						)}
					</div>
				)}

				<AppAlertDialog
					open={!!pendingInviteId}
					onOpenChange={(dialogOpen) => !dialogOpen && setPendingInviteId(null)}
					title="consultantPanel.replaceBookedTitle"
					description="consultantPanel.replaceBookedDescription"
					onConfirm={() => {
						if (pendingInviteId) onInviteChange([...targetInvitedIds, pendingInviteId]);
					}}
				/>
			</FloatingPanelContent>
		</FloatingPanel>
	);
};

export default JobConsultantPanel;
